/**
 * Environmental effect models — driftwing flock, thermalcrawler swarm,
 * impact dust, planet rings, aurora.
 * Ported from design_sheets/environmental_effects.html, simplified for game scale.
 * All models built in local space (Y = up from surface).
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { makeInstancedGroup, type InstancedGroup } from './meshBake';
import type { EnvironmentalEffectType } from '../types';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function mat(color: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color: new THREE.Color(color) });
}

function rand(lo: number, hi: number): number {
  return lo + Math.random() * (hi - lo);
}

// ---------------------------------------------------------------------------
// EffectAnimData — returned alongside the Group for the animation system
// ---------------------------------------------------------------------------

export interface DriftwingBoid {
  mesh: THREE.Object3D;   // proxy driving one instance of the flock's InstancedMesh
  vel: THREE.Vector3;
  home: THREE.Vector3;
  phase: number;
  scattered: boolean;
  scatterTime: number;
}

export interface CrawlerState {
  group: THREE.Group;
  bodyMesh: THREE.Object3D;   // proxy (instanced)
  heading: number;
  vel: { x: number; z: number };
  phase: number;
  retracted: boolean;
  retractTime: number;
  legs: THREE.Object3D[];     // proxies (instanced)
}

/** One aurora band: a run of the shared aurora geometry's vertices (`start`, (segs + 1) × (vertRows + 1) of them). */
export interface AuroraBand {
  start: number;
  basePositions: Float32Array;
  baseColors: Float32Array;
  ringR: number;
  poleY: number;
  segs: number;
  vertRows: number;
  phase: number;
  flowSpeed: number;
  waveSpeed: number;
  pulseSpeed: number;
  opacity: number;
}

export interface ShimmerParticle {
  angle: number;
  dist: number;
  speed: number;
}

export interface EffectAnimData {
  // Driftwing
  boids?: DriftwingBoid[];
  scattered?: boolean;
  scatterStart?: number;
  // Thermalcrawler
  crawlers?: CrawlerState[];
  disturbed?: boolean;
  disturbStart?: number;
  // Aurora: every band in one geometry (one draw), animated in place
  bands?: AuroraBand[];
  auroraGeo?: THREE.BufferGeometry;
  northGlow?: THREE.PointLight;
  southGlow?: THREE.PointLight;
  // Rings
  shimmerParticles?: THREE.Points;
  shimmerData?: ShimmerParticle[];
  ringGroup?: THREE.Group;
  // InstancedMesh groups to sync after the proxies have been animated
  instanced?: InstancedGroup[];
}

// ---------------------------------------------------------------------------
// Impact dust — created/disposed per impact, not per-planet
// ---------------------------------------------------------------------------

export interface ImpactDustEffect {
  points: THREE.Points;
  particles: Array<{
    vel: THREE.Vector3;
    life: number;
    maxLife: number;
    active: boolean;
  }>;
  elapsed: number;
  /** the ground's up at the impact: the dust rises along it and falls back along it */
  up: THREE.Vector3;
  /** the profile's scale (1 = today's sizes) */
  scale: number;
}

/**
 * A puff of dust at an impact. `up` is the ground's normal there (the dust used to rise along the
 * world's y, which is only up at a pole); `scale` is the profile's k — the spread, the lift, the
 * speeds and the grain all shrink with the person (1 keeps today's sizes).
 */
const _noDrift = new THREE.Vector3();
export function createImpactDust(
  scene: THREE.Scene,
  impactPoint: THREE.Vector3,
  up: THREE.Vector3 = new THREE.Vector3(0, 1, 0),
  scale = 1,
  /** 67.36: the wind's drift (world units a second; windView smokeDrift): the dust blows downwind as it settles */
  drift?: THREE.Vector3,
): ImpactDustEffect {
  const dustCount = 40;
  const posArr = new Float32Array(dustCount * 3);
  const particles: ImpactDustEffect['particles'] = [];
  const n = up.clone().normalize();
  const ref = Math.abs(n.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  const t1 = new THREE.Vector3().crossVectors(n, ref).normalize(), t2 = new THREE.Vector3().crossVectors(n, t1);

  for (let i = 0; i < dustCount; i++) {
    const p = impactPoint.clone().addScaledVector(t1, (Math.random() - 0.5) * 0.3 * scale).addScaledVector(t2, (Math.random() - 0.5) * 0.3 * scale).addScaledVector(n, 0.05 * scale);
    posArr[i * 3] = p.x; posArr[i * 3 + 1] = p.y; posArr[i * 3 + 2] = p.z;

    const angle = Math.random() * Math.PI * 2;
    const speed = (0.3 + Math.random() * 1.0) * scale;
    particles.push({
      vel: new THREE.Vector3().addScaledVector(t1, Math.cos(angle) * speed).addScaledVector(t2, Math.sin(angle) * speed).addScaledVector(n, (1.2 + Math.random() * 1.5) * scale).add(drift ?? _noDrift),
      life: 0,
      maxLife: 1.5 + Math.random() * 1.5,
      active: true,
    });
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
  const dustMat = new THREE.PointsMaterial({
    color: new THREE.Color('#a09880'),
    size: 0.1 * scale,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
  });
  const points = new THREE.Points(geo, dustMat);
  scene.add(points);

  return { points, particles, elapsed: 0, up: n, scale };
}

export function updateImpactDust(dust: ImpactDustEffect, dt: number): boolean {
  dust.elapsed += dt;
  const posArr = dust.points.geometry.attributes.position.array as Float32Array;
  let anyActive = false;

  for (let i = 0; i < dust.particles.length; i++) {
    const p = dust.particles[i];
    if (!p.active) continue;
    p.life += dt;
    if (p.life > p.maxLife) {
      p.active = false;
      posArr[i * 3 + 1] = -100; // hide
      continue;
    }
    anyActive = true;
    // Gravity (along the ground's up) + drag
    p.vel.addScaledVector(dust.up, -1.5 * dust.scale * dt);
    p.vel.multiplyScalar(1 - 1.2 * dt);
    posArr[i * 3] += p.vel.x * dt;
    posArr[i * 3 + 1] += p.vel.y * dt;
    posArr[i * 3 + 2] += p.vel.z * dt;
  }

  dust.points.geometry.attributes.position.needsUpdate = true;
  (dust.points.material as THREE.PointsMaterial).opacity = anyActive
    ? Math.max(0.1, 0.5 - dust.elapsed * 0.15)
    : 0;

  return anyActive;
}

export function disposeImpactDust(
  scene: THREE.Scene,
  dust: ImpactDustEffect
): void {
  scene.remove(dust.points);
  dust.points.geometry.dispose();
  (dust.points.material as THREE.Material).dispose();
}

// ============================================================
// 1. Driftwing Flock — translucent diamond boids above surface
// ============================================================

function buildDriftwingFlock(): { group: THREE.Group; animData: EffectAnimData } {
  const g = new THREE.Group();
  const count = 15;
  const boids: DriftwingBoid[] = [];

  // One unit diamond wing (span 1, depth 0.6); each boid's span is its instance scale
  const wingGeo = new THREE.BufferGeometry();
  const depth = 0.6;
  wingGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
    0, 0, depth,
    -1, 0, 0,
    0, 0.005, 0,
    0, 0, depth,
    0, 0.005, 0,
    1, 0, 0,
    0, 0, -depth * 0.3,
    -0.4, 0, 0,
    0.4, 0, 0,
  ]), 3));
  wingGeo.computeVertexNormals();
  // One material for the flock (per-boid opacity was 0.5–0.7; colour stays per instance)
  const wingMat = new THREE.MeshLambertMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.6,
    side: THREE.DoubleSide,
  });

  const proxies: THREE.Object3D[] = [];
  const colors: THREE.Color[] = [];
  for (let i = 0; i < count; i++) {
    const span = 0.04 + Math.random() * 0.02;
    const wing = new THREE.Object3D(); // proxy — the animation code moves this
    wing.scale.set(span, 1, span);
    const angle = Math.random() * Math.PI * 2;
    const dist = 0.3 + Math.random() * 1.0;
    wing.position.set(
      Math.cos(angle) * dist,
      0.6 + Math.random() * 0.8,
      Math.sin(angle) * dist
    );
    g.add(wing);
    proxies.push(wing);
    colors.push(new THREE.Color(
      0.7 + Math.random() * 0.15,
      0.75 + Math.random() * 0.15,
      0.7 + Math.random() * 0.1
    ));

    boids.push({
      mesh: wing,
      vel: new THREE.Vector3(
        (Math.random() - 0.5) * 0.2,
        (Math.random() - 0.5) * 0.05,
        (Math.random() - 0.5) * 0.2
      ),
      home: wing.position.clone(),
      phase: Math.random() * Math.PI * 2,
      scattered: false,
      scatterTime: 0,
    });
  }

  const instanced = [makeInstancedGroup(g, wingGeo, wingMat, proxies, colors)]; // 15 draw calls → 1

  return {
    group: g,
    animData: { boids, scattered: false, scatterStart: 0, instanced },
  };
}


// ============================================================
// 2. Thermalcrawler Swarm — ground-dwelling crawlers
// ============================================================

function buildThermalcrawlerSwarm(): { group: THREE.Group; animData: EffectAnimData } {
  const g = new THREE.Group();
  const count = 8;
  const crawlers: CrawlerState[] = [];

  const bodyGeo = new THREE.SphereGeometry(0.04, 6, 4);
  const bodyMat = new THREE.MeshLambertMaterial({ color: 0xffffff }); // per-crawler colour via instanceColor
  const legGeo = new THREE.CylinderGeometry(0.003, 0.003, 0.02, 4);
  const legMat = mat('#3a3028');

  const bodyProxies: THREE.Object3D[] = [];
  const bodyColors: THREE.Color[] = [];
  const legProxies: THREE.Object3D[] = [];

  for (let i = 0; i < count; i++) {
    const crawler = new THREE.Group();

    // Body — flattened oval (proxy; scale.y is animated when retracting)
    const body = new THREE.Object3D();
    body.scale.set(1.2, 0.4, 1.0);
    crawler.add(body);
    bodyProxies.push(body);
    bodyColors.push(new THREE.Color(
      0.35 + Math.random() * 0.15,
      0.28 + Math.random() * 0.1,
      0.2 + Math.random() * 0.1
    ));

    // 6 legs (proxies; rotation.x and visible are animated)
    const legs: THREE.Object3D[] = [];
    for (let l = 0; l < 6; l++) {
      const side = l < 3 ? -1 : 1;
      const idx = l % 3;
      const leg = new THREE.Object3D();
      leg.position.set(side * 0.03, -0.01, (idx - 1) * 0.025);
      leg.rotation.z = side * 0.4;
      crawler.add(leg);
      legs.push(leg);
      legProxies.push(leg);
    }

    const angle = Math.random() * Math.PI * 2;
    const dist = 0.2 + Math.random() * 0.8;
    crawler.position.set(Math.cos(angle) * dist, 0.02, Math.sin(angle) * dist);
    crawler.rotation.y = Math.random() * Math.PI * 2;
    g.add(crawler);

    crawlers.push({
      group: crawler,
      bodyMesh: body,
      heading: Math.random() * Math.PI * 2,
      vel: { x: (Math.random() - 0.5) * 0.02, z: (Math.random() - 0.5) * 0.02 },
      phase: Math.random() * Math.PI * 2,
      retracted: false,
      retractTime: 0,
      legs,
    });
  }

  // 56 draw calls → 2
  const instanced = [
    makeInstancedGroup(g, bodyGeo, bodyMat, bodyProxies, bodyColors),
    makeInstancedGroup(g, legGeo, legMat, legProxies),
  ];

  return {
    group: g,
    animData: { crawlers, disturbed: false, disturbStart: 0, instanced },
  };
}


// ============================================================
// 3. Planet Rings — banded disc + shimmer particles
// ============================================================

function buildPlanetRings(): { group: THREE.Group; animData: EffectAnimData } {
  const g = new THREE.Group();

  // Ring bands at game scale (planet radius ~3-6, use relative)
  const ringDefs = [
    { inner: 1.3, outer: 1.5, color: '#8a7a60', opacity: 0.3 },
    { inner: 1.55, outer: 1.6, color: '#6a6050', opacity: 0.12 },
    { inner: 1.7, outer: 2.1, color: '#9a8a68', opacity: 0.35 },
    { inner: 2.15, outer: 2.2, color: '#000000', opacity: 0.0 }, // gap
    { inner: 2.25, outer: 2.7, color: '#7a6a50', opacity: 0.25 },
    { inner: 2.8, outer: 2.95, color: '#5a5040', opacity: 0.1 },
  ];

  // One mesh for every band (perf backlog: was a mesh, a material and two transparent passes a band): each band's
  // colour and opacity ride its vertices (RGBA), and the bands are concentric in one plane, so none covers another
  // and the merged mesh draws the same picture
  const ringGroup = new THREE.Group();
  const bandGeos: THREE.BufferGeometry[] = [];
  for (const def of ringDefs) {
    if (def.opacity < 0.01) continue;
    const geo = new THREE.RingGeometry(def.inner, def.outer, 48, 1);
    const c = new THREE.Color(def.color), n = geo.attributes.position.count, rgba = new Float32Array(n * 4);
    for (let i = 0; i < n; i++) { rgba[i * 4] = c.r; rgba[i * 4 + 1] = c.g; rgba[i * 4 + 2] = c.b; rgba[i * 4 + 3] = def.opacity; }
    geo.setAttribute('color', new THREE.BufferAttribute(rgba, 4));
    bandGeos.push(geo);
  }
  const ringMat = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, side: THREE.DoubleSide });
  const ring = new THREE.Mesh(mergeGeometries(bandGeos, false), ringMat);
  for (const geo of bandGeos) geo.dispose();
  ring.rotation.x = Math.PI / 2;
  ringGroup.add(ring);

  // Slight tilt
  ringGroup.rotation.x = 0.12;
  ringGroup.rotation.z = 0.04;
  g.add(ringGroup);

  // Shimmer particles
  const shimmerCount = 60;
  const shimmerGeo = new THREE.BufferGeometry();
  const shimmerPos = new Float32Array(shimmerCount * 3);
  const shimmerData: ShimmerParticle[] = [];

  for (let i = 0; i < shimmerCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist = 1.3 + Math.random() * 1.65;
    shimmerPos[i * 3] = Math.cos(angle) * dist;
    shimmerPos[i * 3 + 1] = (Math.random() - 0.5) * 0.04;
    shimmerPos[i * 3 + 2] = Math.sin(angle) * dist;
    shimmerData.push({
      angle,
      dist,
      speed: 0.06 + (1 - (dist - 1.3) / 1.65) * 0.08,
    });
  }

  shimmerGeo.setAttribute('position', new THREE.BufferAttribute(shimmerPos, 3));
  const shimmerMat = new THREE.PointsMaterial({
    color: new THREE.Color('#c8b888'),
    size: 0.05,
    transparent: true,
    opacity: 0.35,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const shimmerParticles = new THREE.Points(shimmerGeo, shimmerMat);
  shimmerParticles.rotation.x = 0.12;
  shimmerParticles.rotation.z = 0.04;
  g.add(shimmerParticles);

  return {
    group: g,
    animData: { shimmerParticles, shimmerData, ringGroup },
  };
}

// ============================================================
// 4. Aurora — toroidal ribbon bands wrapping planet poles
// ============================================================

function buildAurora(): { group: THREE.Group; animData: EffectAnimData } {
  const g = new THREE.Group();
  const R = 1.85; // reference planet radius (scene placement scales to actual)

  const bands: AuroraBand[] = [];
  const allVerts: number[] = [], allColors: number[] = [], allIndices: number[] = [];
  const bandConfigs = [
    // Northern bands
    { lat: 70, radius: 0.65, height: 0.5, segments: 80, color1: [0.1, 0.9, 0.4], color2: [0.1, 0.4, 0.9], opacity: 0.12 },
    { lat: 75, radius: 0.50, height: 0.6, segments: 64, color1: [0.2, 0.8, 0.5], color2: [0.3, 0.3, 0.8], opacity: 0.10 },
    { lat: 65, radius: 0.80, height: 0.4, segments: 72, color1: [0.15, 1.0, 0.3], color2: [0.5, 0.2, 0.7], opacity: 0.08 },
    { lat: 80, radius: 0.35, height: 0.7, segments: 48, color1: [0.3, 0.6, 0.9], color2: [0.6, 0.1, 0.6], opacity: 0.09 },
    // Southern bands (dimmer)
    { lat: -72, radius: 0.58, height: 0.4, segments: 60, color1: [0.15, 0.7, 0.5], color2: [0.4, 0.2, 0.7], opacity: 0.07 },
    { lat: -78, radius: 0.40, height: 0.5, segments: 48, color1: [0.2, 0.6, 0.6], color2: [0.3, 0.3, 0.8], opacity: 0.06 },
  ];

  for (const cfg of bandConfigs) {
    const latRad = (cfg.lat * Math.PI) / 180;
    const poleY = Math.sin(latRad) * R;
    const ringR = Math.cos(latRad) * R + cfg.radius;

    const segs = cfg.segments;
    const vertRows = 6;
    const verts: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];

    for (let i = 0; i <= segs; i++) {
      const theta = (i / segs) * Math.PI * 2;
      const cx = Math.cos(theta) * ringR;
      const cz = Math.sin(theta) * ringR;

      for (let j = 0; j <= vertRows; j++) {
        const v = j / vertRows;
        const h = (v - 0.5) * cfg.height;
        verts.push(cx, poleY + h, cz);

        // Green-to-blue gradient with edge fade
        const r = cfg.color1[0] * (1 - v) + cfg.color2[0] * v;
        const gv = cfg.color1[1] * (1 - v) + cfg.color2[1] * v;
        const b = cfg.color1[2] * (1 - v) + cfg.color2[2] * v;
        const edgeFade = Math.sin(v * Math.PI);
        colors.push(r * edgeFade, gv * edgeFade, b * edgeFade);
      }
    }

    for (let i = 0; i < segs; i++) {
      for (let j = 0; j < vertRows; j++) {
        const a = i * (vertRows + 1) + j;
        const b = a + vertRows + 1;
        indices.push(a, b, a + 1, b, b + 1, a + 1);
      }
    }

    const start = allVerts.length / 3;
    allVerts.push(...verts);
    allColors.push(...colors);
    for (const i of indices) allIndices.push(start + i);

    bands.push({
      start,
      basePositions: new Float32Array(verts),
      baseColors: new Float32Array(colors),
      ringR, poleY, segs, vertRows,
      phase: Math.random() * Math.PI * 2,
      flowSpeed: 0.4 + Math.random() * 0.3,
      waveSpeed: 0.15 + Math.random() * 0.15,
      pulseSpeed: 0.1 + Math.random() * 0.1,
      opacity: cfg.opacity,
    });
  }

  // One mesh for every band (perf backlog: was a mesh, a material and two transparent passes a band). The bands are
  // additive, so a band's opacity is a factor on its colour: the animation writes opacity × pulse into the vertex
  // colours and the material stays at 1 — the same light added to the frame as six separate bands.
  const auroraGeo = new THREE.BufferGeometry();
  auroraGeo.setAttribute('position', new THREE.Float32BufferAttribute(allVerts, 3));
  const auroraColors = new Float32Array(allColors);
  for (const b of bands) for (let i = 0; i < b.baseColors.length; i++) auroraColors[b.start * 3 + i] = b.baseColors[i] * b.opacity;
  auroraGeo.setAttribute('color', new THREE.BufferAttribute(auroraColors, 3));
  auroraGeo.setIndex(allIndices);
  const auroraMat = new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  g.add(new THREE.Mesh(auroraGeo, auroraMat));

  // No polar point lights: the bands are additive and bloom does the rest.

  return {
    group: g,
    animData: { bands, auroraGeo },
  };
}

// ============================================================
// Dispatcher
// ============================================================

type EffectBuilder = () => { group: THREE.Group; animData: EffectAnimData };

const EFFECT_BUILDERS: Record<EnvironmentalEffectType, EffectBuilder> = {
  driftwingFlock: buildDriftwingFlock,
  thermalcrawlerSwarm: buildThermalcrawlerSwarm,
  planetRings: buildPlanetRings,
  aurora: buildAurora,
};

export function buildEnvironmentalEffect(
  effectType: EnvironmentalEffectType
): { group: THREE.Group; animData: EffectAnimData } {
  return EFFECT_BUILDERS[effectType]();
}
